import {assertNotNullable, ObservableValue} from '@wix/devzai-utils-common';
import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import type {IDoppeViewerApi} from '../../client-server-common/api/doppe-viewer-api';

const CART_ID_STORAGE_KEY = 'doppe-cart-id';

/**
 * Client service for managing the eCommerce cart in the viewer.
 * Persists cartId in localStorage and exposes an ObservableValue for reactive subscription.
 * Cart is created lazily on the first addToCart call.
 */
export class DoppeCartClientService extends DoppeViewerAppClientServiceBase {

    /** Observable cart ID — subscribe via useObservableValue for reactive updates. */
    public readonly observableCartId = new ObservableValue<string | null>(doppeCartClientServiceStorageGetCartId());

    /**
     * Add a catalog line item to the cart.
     * Lazily creates a new cart if none exists.
     * Returns the cartId after the operation completes.
     */
    public async addToCart(lineItem: IDoppeViewerApi.CartCatalogLineItemInput): Promise<string> {
        const currentCartId = this.observableCartId.getValue();

        if (currentCartId) {
            await this.apiClient.addToCart({
                cartId: currentCartId,
                lineItems: [lineItem],
            });
            return currentCartId;
        } else {
            const result = await this.apiClient.createCart({
                lineItems: [lineItem],
            });
            const newCartId = assertNotNullable(result.cart?.id, 'Cart creation did not return an ID');
            doppeCartClientServiceStorageSetCartId(newCartId);
            this.observableCartId.setValue(newCartId);
            return newCartId;
        }
    }

    /** Clear the cart (e.g. after checkout). Removes cartId from observable and storage. */
    public clearCart(): void {
        doppeCartClientServiceStorageClearCartId();
        this.observableCartId.setValue(null);
    }

    /** Returns the current cart ID, or null if no cart exists. */
    public getCartId(): string | null {
        return this.observableCartId.getValue();
    }
}

// ============================================================================
// SSR-safe localStorage helpers
// ============================================================================

function doppeCartClientServiceStorageGetCartId(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return localStorage.getItem(CART_ID_STORAGE_KEY);
    } catch {
        return null;
    }
}

function doppeCartClientServiceStorageSetCartId(cartId: string): void {
    try {
        localStorage.setItem(CART_ID_STORAGE_KEY, cartId);
    } catch {
        // localStorage might be unavailable
    }
}

function doppeCartClientServiceStorageClearCartId(): void {
    try {
        localStorage.removeItem(CART_ID_STORAGE_KEY);
    } catch {
        // Ignore
    }
}
